import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { fetchUnreadChats } from '../services/chat.api';
import { getAccessToken } from '../services/auth';
import { chatSocketService } from '../services/chat.socket';

interface UnreadMessagesContextType {
  hasUnreadMessages: boolean;
  checkUnreadMessages: () => Promise<void>;
  clearUnreadMessages: () => void;
}

const UnreadMessagesContext = createContext<UnreadMessagesContextType | undefined>(undefined);

export const useUnreadMessages = () => {
  const context = useContext(UnreadMessagesContext);
  if (!context) {
    throw new Error('useUnreadMessages must be used within UnreadMessagesProvider');
  }
  return context;
};

interface UnreadMessagesProviderProps {
  children: ReactNode;
  isLoggedIn: boolean;
}

export const UnreadMessagesProvider: React.FC<UnreadMessagesProviderProps> = ({ children, isLoggedIn }) => {
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);

  const checkUnreadMessages = async () => {
    if (!isLoggedIn || !getAccessToken()) {
      setHasUnreadMessages(false);
      return;
    }

    try {
      const response = await fetchUnreadChats();
      setHasUnreadMessages(response.hasUnread);
    } catch (error) {
      console.error('Error checking unread messages:', error);
    }
  };

  const clearUnreadMessages = () => {
    setHasUnreadMessages(false);
  };

  useEffect(() => {
    if (isLoggedIn) {
      void checkUnreadMessages();
      void chatSocketService.connect();
      const unsubscribe = chatSocketService.subscribe((event) => {
        if (typeof event.unreadCount === 'number') {
          setHasUnreadMessages(event.unreadCount > 0);
        }
      });
      let skipNextConnectedRefresh = true;
      const unsubscribeStatus = chatSocketService.subscribeStatus((connected) => {
        if (connected) {
          if (skipNextConnectedRefresh) {
            skipNextConnectedRefresh = false;
            return;
          }
          void checkUnreadMessages();
        }
      });
      
      const interval = setInterval(() => {
        if (!chatSocketService.isConnected()) {
          void checkUnreadMessages();
        }
      }, 60000);
      
      return () => {
        unsubscribe();
        unsubscribeStatus();
        clearInterval(interval);
      };
    } else {
      chatSocketService.disconnect();
      setHasUnreadMessages(false);
    }
  }, [isLoggedIn]);

  return (
    <UnreadMessagesContext.Provider value={{ hasUnreadMessages, checkUnreadMessages, clearUnreadMessages }}>
      {children}
    </UnreadMessagesContext.Provider>
  );
};
